import React, { useEffect, useMemo, useState } from "react"
import {
  graphSampleNodeToViewGroupNodeLabel,
  graphViewGroupNodeLabelToSampleNode,
} from "@features/graph/utils/graphUtils"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import { TimeSeriesContainer } from "@features/results/Result/ChartsContainer/TimeSeriesContainer"
import { RunDto, RunResultsHistoric } from "@custom-types/modelRun"
import { Box, Stack } from "@mui/material"
import ToggleNodeChips from "@features/results/Result/ToggleNodeChips"
import { calculateLagShift } from "@utils/run"
import { useGetRunsNodesResultsHistoricAggregateQuery } from "@store/services/runService"

type TimeSeriesMultipleNodesContainerProps = {
  selectedNodes: string[]
  isLoading: boolean
  run?: RunDto
  historicNodes?: RunResultsHistoric
}

export function TimeSeriesMultipleNodesContainer({
  selectedNodes,
  isLoading,
  run,
  historicNodes,
}: TimeSeriesMultipleNodesContainerProps) {
  const [nodeLabels, setNodeLabels] = useState<GroupNodeLabel[]>([])
  const [isChartVisible, setIsChartVisible] = useState(false)
  const [visibleNodeLabels, setVisibleNodeLabels] = useState<Set<string>>(new Set())

  const lagShift = useMemo(() => calculateLagShift(historicNodes), [historicNodes])

  const { isFetching, data: aggregateData } = useGetRunsNodesResultsHistoricAggregateQuery(
    {
      runKeys: [run?.key ?? ""],
      nodes: selectedNodes.map(graphViewGroupNodeLabelToSampleNode),
    },
    {
      skip: !run || selectedNodes.length === 0,
    },
  )

  useEffect(() => {
    const result = selectedNodes.map((x) => graphSampleNodeToViewGroupNodeLabel(x))

    setNodeLabels(result)
  }, [selectedNodes])

  useEffect(() => {
    const formatCount = new Set()
    nodeLabels.forEach((nodeLabel) => {
      if (nodeLabel.format) {
        formatCount.add(nodeLabel.format)
      }
    })
    setVisibleNodeLabels(new Set(nodeLabels.map((x) => x.label)))
    setIsChartVisible(formatCount.size <= 2)
  }, [nodeLabels])

  const handleChipToggle = (nodeLabel: string) => {
    const newSet = new Set(visibleNodeLabels)
    newSet.has(nodeLabel) ? newSet.delete(nodeLabel) : newSet.add(nodeLabel)

    const formatCount = new Set()
    newSet.forEach((nodeLabel) => {
      const node = nodeLabels.find((x) => x.label === nodeLabel)
      if (node && node.format) {
        formatCount.add(node.format)
      }
    })
    setIsChartVisible(formatCount.size <= 2)

    setVisibleNodeLabels(newSet)
  }

  const parsedVisibleNodeLabels = Array.from(visibleNodeLabels).map(
    graphViewGroupNodeLabelToSampleNode,
  )
  const filteredNodeResults = structuredClone(aggregateData)
  Object.entries(filteredNodeResults?.results ?? {}).forEach(([runKey, runsResults]) => {
    Object.entries(runsResults).forEach(([nodeKey]) => {
      if (!parsedVisibleNodeLabels.includes(nodeKey)) {
        delete filteredNodeResults?.results?.[runKey]?.[nodeKey]
        return
      }
    })
  })

  const filteredHistoricNodes = structuredClone(historicNodes)
  Object.entries(filteredHistoricNodes ?? {}).forEach(([runKey, runsResults]) => {
    Object.entries(runsResults).forEach(([nodeKey]) => {
      if (!parsedVisibleNodeLabels.includes(nodeKey)) {
        // @ts-expect-error - possibly empty object, which is safe, but typesciprt complains about accessing property of it
        delete filteredHistoricNodes?.[runKey]?.[nodeKey]
        return
      }
    })
  })

  return (
    <>
      <Stack direction="row" gap={1} sx={{ flexWrap: "wrap" }}>
        <ToggleNodeChips
          nodeLabels={nodeLabels.map((nodeLabel) => nodeLabel.label)}
          selectedNodeLabels={Array.from(visibleNodeLabels)}
          onToggle={handleChipToggle}
        />
      </Stack>
      {isChartVisible && visibleNodeLabels.size > 0 ? (
        <TimeSeriesContainer
          runs={run ? [run] : []}
          isFetchingGetRunFilterNode={isLoading || isFetching}
          aggregateData={filteredNodeResults}
          lagShift={lagShift}
          nodes={nodeLabels.filter((x) => visibleNodeLabels.has(x.label))}
          localStoragePrefix={"rawResultMultipleNodes"}
          isMultipleNodesMode={true}
          historicNodes={filteredHistoricNodes}
        />
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          {visibleNodeLabels.size === 0
            ? "Please, re-select some nodes in tags above"
            : "Can't render the chart, as selected Nodes have different units. Disable some nodes in tags above"}
        </Box>
      )}
    </>
  )
}
